import { getUserProfile } from "./authUtils";
import { OperationType, UserPermissions } from "../types/operationType";

export interface PlanStatus {
  daysUntilExpiry: number;
  isExpired: boolean;
  isNotRegistered: boolean;
  isExpiringSoon: boolean;
  isNewUnpaidUser: boolean;
  isNewPaidUser: boolean;
  canAccess: boolean;
  planType: string;
  companyId: string | null;
  company: any;
  operationType: OperationType;
  user?: any;
  permissions?: UserPermissions;
}

export const getPlanStatus = async (): Promise<PlanStatus> => {
  try {
    const userData = await getUserProfile();
    const isNewUnpaidUser =
      userData?.company?.verificationStatus === "PENDING" &&
      userData?.company?.planType === "ACTIVATION" &&
      userData?.company?.planExpiry === null;
    const isNewPaidUser = userData?.company?.vehicleCount > 0;

    const isNotRegistered = userData?.company?.id === null;

    if (!userData?.company?.planExpiry) {
      return {
        daysUntilExpiry: 0,
        isExpired: true,
        isNotRegistered: isNotRegistered,
        isExpiringSoon: true,
        isNewUnpaidUser: isNewUnpaidUser,
        isNewPaidUser: isNewPaidUser,
        canAccess: false,
        planType: userData?.company?.planType,
        companyId: userData?.company?.id,
        company: userData?.company,
        operationType: userData?.company?.operationType,
        user: userData,
        permissions: userData?.permissions,
      };
    }

    const expiryDate = new Date(userData.company.planExpiry);
    const currentDate = new Date();

    // Set both dates to start of day (midnight) to ignore time
    expiryDate.setHours(0, 0, 0, 0);
    currentDate.setHours(0, 0, 0, 0);

    const timeDiff = expiryDate.getTime() - currentDate.getTime();
    const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));

    return {
      daysUntilExpiry: daysDiff,
      isExpired: daysDiff <= 0,
      isNotRegistered: isNotRegistered,
      isExpiringSoon: daysDiff <= 3,
      isNewUnpaidUser: isNewUnpaidUser,
      isNewPaidUser: isNewPaidUser,
      canAccess: daysDiff > 0,
      planType: userData?.company?.planType,
      companyId: userData?.company?.id,
      company: userData?.company,
      operationType: userData?.company?.operationType,
      user: userData,
      permissions: userData?.permissions,
    };
  } catch (error) {
    return {
      daysUntilExpiry: 0,
      isExpired: false,
      isNotRegistered: true,
      isExpiringSoon: false,
      isNewUnpaidUser: false,
      isNewPaidUser: false,
      canAccess: true,
      planType: "free",
      companyId: null,
      company: null,
      operationType: OperationType.ALL,
      user: null,
      permissions: undefined,
    };
  }
};

export const showExpiryAlert = (planStatus: PlanStatus) => {
  if (planStatus.isNotRegistered) {
    return "You are not registered with us. Please register to continue using the app.";
  }
  if (planStatus.isExpired) {
    return "Your Free trial has expired! Please upgrade your plan to continue using the app. All access is currently blocked.";
  }

  if (planStatus.isExpiringSoon) {
    return `URGENT: Your Free trial expires in ${
      planStatus.daysUntilExpiry
    } day${
      planStatus.daysUntilExpiry !== 1 ? "s" : ""
    }! Upgrade now to avoid service interruption.`;
  }

  return null;
};

export const shouldBlockAccess = (
  planStatus: PlanStatus,
  allowDashboard: boolean = false
): boolean => {
  if (planStatus.isExpired) {
    return !allowDashboard; // Only allow dashboard for expired users
  }
  return false;
};
